/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description Creates a MEA Query from database
 */
import axios, { AxiosResponse } from "axios";
import { uniqBy } from "lodash";
import { QueryKey, useQuery, UseQueryOptions } from "react-query";
import isDraftFilterAllowedAtLocation from "./../attributes/drafts/disabledPages";
import augmentQueryArguments from "./../attributes/parser/arguments";
import filterResultsetRow from "./../attributes/parser/resultset";
import transformJSONRows from "./transformJSONRows";
/**
 *
 * Example:
 ```typescript
 const {isLoading, data } = useMeaQuery("[SC.1234.5678]", "name, symbol");
 ```
 *
 * @param queryString
 * @param attributes
 * @param sortBy
 * @param options
 * @param jsonmode
 * @param queryKey
 * @param criteria
 * @returns
 */
const useMeaQuery = <T>(
  queryString: string,
  attributes: string,
  sortBy?: string,
  options?:
    | Omit<
        UseQueryOptions<
          {
            data: T;
          },
          unknown,
          {
            data: T;
          },
          QueryKey
        >,
        "queryKey" | "queryFn"
      >
    | undefined,
  jsonmode?: number | undefined,
  queryKey?: string,
  criteria?: string,
  queryOptions?: object
) => {
  const restQueryOptions = { ...((queryOptions as any) || {}) };
  delete (restQueryOptions as any).status;
  const key = `meaquery-${queryString}-${attributes}-${sortBy || ""}-${
    jsonmode || ""
  }-${queryKey || ""}`;
  const defaultValues = {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep cached data for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus to improve performance
    refetchOnMount: false, // Don't refetch on mount if data is still fresh
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled:
      !queryString.includes("[undefined]") && 
      !queryString.includes("[null]") &&
      !queryString.includes("[]") &&
      !queryString.match(/\[SC\.\]/) &&
      !queryString.match(/\[SC\.\s*\]/) &&
      queryString.trim().length > 0,
  };
  return useQuery<{
    data: T;
  }>(
    `${key}-${isDraftFilterAllowedAtLocation()}`,
    async () => {
      /**
       * Add status flags to each period in the attribute set
       */
      // Guard: react-query v3 refetchQueries can invoke enabled:false queries.
      if (
        !queryString?.trim() ||
        queryString.includes("[]") ||
        queryString.includes("[undefined]") ||
        queryString.includes("[null]")
      ) {
        return { data: [] as unknown as T };
      }
      const { newSet, disableFilter, newQueryAttrs } =
        augmentQueryArguments(attributes);
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      try {
        const response: AxiosResponse<T> = await axios.post<T>("meaquery", {
          query: queryString,
          attributes: newQueryAttrs,
          sortBy,
          jsonmode,
          criteria,
          ...restQueryOptions,
        }, {
          signal: controller.signal,
          timeout: 60000, // 60 second timeout
        });
        clearTimeout(timeoutId);
        return processResponse(response);
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
          throw new Error('Request timeout: The query took too long to complete');
        }
        throw error;
      }
      
      function processResponse(response: AxiosResponse<T>) {
        const data = response.data as any;
        if (Array.isArray(data.data) && !disableFilter) {
          data.data = data.data.filter((row: any) => {
            filterResultsetRow(newSet, row);
            return true;
          });
        }
        transformJSONRows(response);
        if (Array.isArray(data.data)) {
          /**
           * If name is asked but no name is returned, filter away those items because they are items
           * where we do not have access rights
           */
          const attrList = attributes.split(",").map((v) => v.trim());
          if (attrList.includes("name")) {
            data.data = data.data.filter((elem: any) => elem.name);
          }
          return { data: uniqBy(data.data as any, "id") } as unknown as {
            data: T;
          };
        }
        return response.data as unknown as {
          data: T;
        };
      }
    },
    options ? { ...defaultValues, ...options } : defaultValues
  );
};
export default useMeaQuery;
